import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import { FiX, FiCheck, FiInfo, FiChevronLeft, FiChevronRight, FiArrowLeft, FiArrowRight } from 'react-icons/fi';
import { cn } from '../utils/cn';
import { hallsData } from '../data/halls';

export default function HallModal({ hall, isOpen, onClose, onNavigate }) {
  const [activeImgIndex, setActiveImgIndex] = useState(0);

  useEffect(() => {
    setActiveImgIndex(0);
    // Scroll to top of modal when hall changes
    const modalElement = document.getElementById('hall-modal-content');
    if (modalElement) modalElement.scrollTo({ top: 0, behavior: 'smooth' });
  }, [hall?.id]);

  if (!hall) return null;

  const images = hall.gallery || [hall.image];
  const hasMultipleImages = images.length > 1;

  const nextImage = () => setActiveImgIndex((prev) => (prev + 1) % images.length);
  const prevImage = () => setActiveImgIndex((prev) => (prev - 1 + images.length) % images.length);

  // Navigation Logic
  const hallIds = Object.keys(hallsData);
  const currentIndex = hallIds.indexOf(hall.id);
  const prevHallId = hallIds[currentIndex - 1];
  const nextHallId = hallIds[currentIndex + 1];

  const handleNavigate = (id) => {
    if (onNavigate) {
      onNavigate(id);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 z-[60] bg-emerald-950/80 backdrop-blur-sm"
          />
          <motion.div 
            initial={{ x: '100%', opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: '100%', opacity: 0 }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            id="hall-modal-content"
            className="fixed top-0 right-0 bottom-0 z-[70] w-full max-w-md md:max-w-xl bg-white shadow-2xl overflow-y-auto"
          >
            <div className="relative">
              <button 
                onClick={onClose}
                className="absolute flex items-center justify-center w-10 h-10 transition-colors bg-white/90 rounded-full shadow-md top-4 right-4 text-emerald-900 hover:bg-white z-20"
              >
                <SafeIcon icon={FiX} className="text-xl" />
              </button>

              {/* Image Gallery Header */}
              <div className="relative h-64 bg-gray-200 md:h-80 overflow-hidden">
                <AnimatePresence mode="wait">
                  <motion.img 
                    key={activeImgIndex}
                    src={images[activeImgIndex]} 
                    alt={`${hall.title} - Ansicht ${activeImgIndex + 1}`}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="object-cover w-full h-full"
                  />
                </AnimatePresence>

                {hasMultipleImages && (
                  <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 flex justify-between px-4 z-10 pointer-events-none">
                    <button 
                      onClick={(e) => { e.stopPropagation(); prevImage(); }}
                      className="p-2 rounded-full bg-black/20 text-white hover:bg-black/40 transition-colors pointer-events-auto backdrop-blur-sm"
                    >
                      <SafeIcon icon={FiChevronLeft} className="text-xl" />
                    </button>
                    <button 
                      onClick={(e) => { e.stopPropagation(); nextImage(); }}
                      className="p-2 rounded-full bg-black/20 text-white hover:bg-black/40 transition-colors pointer-events-auto backdrop-blur-sm"
                    >
                      <SafeIcon icon={FiChevronRight} className="text-xl" />
                    </button>
                  </div>
                )}

                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-emerald-950/90 to-transparent p-6 md:p-8">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="flex items-center justify-center w-8 h-8 font-bold rounded-sm text-emerald-900 bg-yellow-500">{hall.id}</span>
                      <span className={cn(
                        "px-3 py-1 text-xs font-semibold uppercase rounded-full",
                        hall.status === 'available' ? "bg-green-500/20 text-green-300 border border-green-500/30" : "bg-red-500/20 text-red-300 border border-red-500/30"
                      )}>
                        {hall.status === 'available' ? 'Verfügbar' : 'Vermietet'}
                      </span>
                    </div>
                    {hasMultipleImages && (
                      <div className="flex gap-1.5">
                        {images.map((_, i) => (
                          <div key={i} className={cn("w-2 h-2 rounded-full transition-all", i === activeImgIndex ? "bg-yellow-500 w-4" : "bg-white/40")} />
                        ))}
                      </div>
                    )}
                  </div>
                  <h3 className="text-3xl font-bold text-white mt-2">{hall.title}</h3>
                </div>
              </div>

              <div className="p-6 md:p-8">
                {hall.status === 'unavailable' ? (
                  <div className="p-6 text-center border rounded-sm bg-gray-50 border-gray-200">
                    <SafeIcon icon={FiInfo} className="mx-auto mb-3 text-3xl text-gray-400" />
                    <p className="text-gray-600">{hall.description}</p>
                  </div>
                ) : (
                  <>
                    <p className="mb-8 text-lg leading-relaxed text-gray-700">{hall.description}</p>
                    
                    <div className="mb-8">
                      <h4 className="mb-4 text-xl font-bold text-emerald-950">Details & Ausstattung</h4>
                      <ul className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {hall.specs.map((spec, index) => (
                          <li key={index} className="flex items-start gap-3 p-3 bg-gray-50 border border-gray-100 rounded-sm">
                            <SafeIcon icon={FiCheck} className="mt-1 text-yellow-600 shrink-0" />
                            <div>
                              <span className="block text-xs font-bold uppercase tracking-wider text-gray-400">{spec.label}</span>
                              <span className="text-sm font-semibold text-gray-900">{spec.value}</span>
                            </div>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className="pt-6 border-t border-gray-100">
                      <a href="#kontakt" onClick={onClose} className="w-full btn-primary">Halle {hall.id} anfragen</a>
                    </div>
                  </>
                )}

                {/* MODAL NAVIGATION */}
                <div className="mt-12 pt-8 border-t border-gray-100">
                  <h4 className="mb-6 text-sm font-bold uppercase tracking-widest text-gray-400 text-center">Nächstes Gebäude entdecken</h4>
                  <div className="grid grid-cols-2 gap-4">
                    {/* Previous Hall */}
                    <button
                      disabled={!prevHallId}
                      onClick={() => handleNavigate(prevHallId)}
                      className={cn(
                        "flex flex-col items-start p-4 border rounded-sm transition-all text-left group",
                        prevHallId 
                          ? "border-gray-200 hover:border-yellow-500 hover:bg-gray-50 cursor-pointer" 
                          : "border-gray-100 opacity-30 cursor-not-allowed"
                      )}
                    >
                      <span className="flex items-center gap-2 text-xs font-bold text-gray-400 uppercase mb-1">
                        <SafeIcon icon={FiArrowLeft} /> Zurück
                      </span>
                      <span className="text-sm font-bold text-emerald-950 truncate w-full">
                        {prevHallId ? `${prevHallId}: ${hallsData[prevHallId].title}` : 'Kein Gebäude'}
                      </span>
                    </button>

                    {/* Next Hall */}
                    <button
                      disabled={!nextHallId}
                      onClick={() => handleNavigate(nextHallId)}
                      className={cn(
                        "flex flex-col items-end p-4 border rounded-sm transition-all text-right group",
                        nextHallId 
                          ? "border-gray-200 hover:border-yellow-500 hover:bg-gray-50 cursor-pointer" 
                          : "border-gray-100 opacity-30 cursor-not-allowed"
                      )}
                    >
                      <span className="flex items-center gap-2 text-xs font-bold text-gray-400 uppercase mb-1">
                        Weiter <SafeIcon icon={FiArrowRight} />
                      </span>
                      <span className="text-sm font-bold text-emerald-950 truncate w-full">
                        {nextHallId ? `${nextHallId}: ${hallsData[nextHallId].title}` : 'Ende'}
                      </span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}